/**
 * Impuestos del checkout, con Stripe Tax en la cuenta del negocio.
 *
 * Una fila de «Tu cuenta» y una ventana:
 * - la fila dice en una línea cómo están —activos, apagados o a medio
 *   configurar— y lleva el interruptor, que es nuestro: decide si la sesión de
 *   pago lleva `automatic_tax`. No se deja encender hasta que Stripe dé la
 *   configuración por completa —con ella a medias, cada intento de pago
 *   fallaría—;
 * - «Configurar» abre la configuración fiscal de Stripe en una ventana:
 *   domicilio, si los precios llevan el impuesto dentro y dónde está registrado
 *   para recaudar. Era una tarjeta propia con el formulario plegado debajo, y
 *   ocupaba media pantalla para algo que se configura una vez.
 *
 * El impuesto es obligación del negocio, no nuestra, y Stripe Tax le cobra su
 * comisión a su cuenta. Por eso nace apagado y lo decide él.
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ConnectTaxRegistrations, ConnectTaxSettings } from '@stripe/react-connect-js'
import { IconReceiptTax, IconX } from '@tabler/icons-react'
import { getTaxSettings, setAutomaticTax, type TaxSettingsStatus } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'
import { EmbedLoading, StripeEmbedProvider, embedErrorMessage } from './StripeEmbed'
import { errorMessage } from './offerShared'

export function StripeTaxSettings({ projectId, token }: { projectId: string; token: string }) {
  const { t } = useI18n()
  const [tax, setTax] = useState<TaxSettingsStatus | null>(null)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setTax(await getTaxSettings(projectId, token))
      setLoadError('')
    } catch (failure) {
      setLoadError(errorMessage(failure))
    }
  }, [projectId, token])

  useEffect(() => { void load() }, [load])

  const toggle = async (next: boolean) => {
    if (saving) return
    setSaving(true)
    setSaveError('')
    try {
      setTax(await setAutomaticTax(projectId, next, token))
    } catch (failure) {
      setSaveError(errorMessage(failure))
    } finally {
      setSaving(false)
    }
  }

  const ready = tax?.status === 'active'
  /* El estado en una línea: lo que hay que saber sin abrir nada. */
  const summary = loadError || saveError
    ? loadError || saveError
    : !tax
      ? t('stripe.checking')
      : !ready
        ? t('stripe.tax.rowIncomplete')
        : tax.enabled
          ? t('stripe.tax.rowOn')
          : t('stripe.tax.rowOff')

  return (
    <>
      <div className="dash-stripe-account-row">
        <IconReceiptTax size={18} aria-hidden="true" />
        <span>
          <strong>{t('stripe.tax.title')}</strong>
          <small className={tax && !ready ? 'is-warn' : undefined}>{summary}</small>
        </span>
        <button type="button" className="dash-ghost-btn" onClick={() => setOpen(true)}>
          {t('stripe.tax.configure')}
        </button>
        {tax ? (
          <button
            className={`dash-general-toggle${tax.enabled ? ' is-on' : ''}`}
            type="button"
            role="switch"
            aria-checked={tax.enabled}
            aria-label={t('stripe.tax.toggle')}
            disabled={saving || (!tax.enabled && !ready)}
            onClick={() => { void toggle(!tax.enabled) }}
          >
            <span />
          </button>
        ) : null}
      </div>
      {open ? (
        <StripeTaxDialog
          projectId={projectId}
          token={token}
          onChanged={() => { void load() }}
          /* Al cerrar se relee: el componente de Stripe no avisa de todo lo que
             se guarda, y la fila tiene que dejar de decir «a medias» cuando ya
             no lo está. */
          onClose={() => { setOpen(false); void load() }}
        />
      ) : null}
    </>
  )
}

/** La configuración fiscal de Stripe, en la misma ventana que las disputas. */
export function StripeTaxDialog({ projectId, token, onChanged, onClose }: {
  projectId: string
  token: string
  onChanged: () => void
  onClose: () => void
}) {
  const { t } = useI18n()
  const [failed, setFailed] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      className="dash-modal-backdrop dash-stripe-embed-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div className="dash-stripe-embed dash-stripe-tax-dialog" role="dialog" aria-modal="true" aria-label={t('stripe.tax.title')}>
        <button type="button" className="dash-stripe-embed-close" aria-label={t('stripe.embed.close')} onClick={onClose}>
          <IconX size={16} />
        </button>
        <header className="dash-stripe-tax-dialog-head">
          <h2>{t('stripe.tax.title')}</h2>
          <p>{t('stripe.tax.subtitle')}</p>
        </header>
        {failed ? (
          <p className="dash-order-detail-note">{failed}</p>
        ) : (
          <div className="dash-stripe-tax-embed">
            <StripeEmbedProvider
              projectId={projectId}
              token={token}
              fallback={<EmbedLoading label={t('stripe.tax.opening')} />}
              onFail={setFailed}
            >
              <ConnectTaxSettings
                onTaxSettingsUpdated={onChanged}
                onLoadError={(event) => {
                  console.error('[stripe] no cargó la configuración fiscal', event.error)
                  setFailed(embedErrorMessage(event.error, 'stripe.embed.subject.account', t))
                }}
              />
              <ConnectTaxRegistrations
                onAfterTaxRegistrationAdded={onChanged}
                onLoadError={(event) => {
                  console.error('[stripe] no cargaron los registros fiscales', event.error)
                  setFailed(embedErrorMessage(event.error, 'stripe.embed.subject.account', t))
                }}
              />
            </StripeEmbedProvider>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
